import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { notificationsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, showToast, showConfirm } from '../../utils/helpers';
import { 
  BellIcon, 
  CheckIcon, 
  TrashIcon, 
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const NotificationsPage = () => {
  const { isRTL } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [markingAll, setMarkingAll] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter === 'unread') {
        params.unread_only = 'true';
      }
      
      const response = await notificationsAPI.getAll(params);
      setNotifications(response.data.notifications || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getCount();
      const count = response.data.unread_count || 0;
      setUnreadCount(count);
      // Update Navbar badge by triggering a custom event
      window.dispatchEvent(new CustomEvent('notificationsUpdated', { detail: { count } }));
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const handleMarkAllAsRead = async () => {
    const confirmed = await showConfirm(
      isRTL 
        ? 'هل أنت متأكد من وضع علامة مقروء لجميع الإشعارات؟' 
        : 'Are you sure you want to mark all notifications as read?',
      isRTL
    );

    if (!confirmed) return;

    try {
      setMarkingAll(true);
      await notificationsAPI.markAllAsRead();
      showToast.success(isRTL ? 'تم وضع علامة مقروء لجميع الإشعارات' : 'All notifications marked as read');
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsAPI.delete(id);
      showToast.success(isRTL ? 'تم حذف الإشعار' : 'Notification deleted');
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const handleClearRead = async () => {
    const confirmed = await showConfirm(
      isRTL 
        ? 'هل أنت متأكد من حذف جميع الإشعارات المقروءة؟' 
        : 'Are you sure you want to clear all read notifications?',
      isRTL
    );

    if (!confirmed) return;

    try {
      setClearing(true);
      await notificationsAPI.clearRead();
      showToast.success(isRTL ? 'تم حذف الإشعارات المقروءة' : 'Read notifications cleared');
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setClearing(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'comment':
        return <ChatBubbleLeftIcon className="h-5 w-5 text-blue-500" />;
      case 'approval':
      case 'status_change':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'new_post':
      case 'new_version':
      case 'new_design_request':
        return <DocumentTextIcon className="h-5 w-5 text-purple-500" />;
      case 'mention':
        return <UserGroupIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNotificationColor = (type, isRead) => {
    if (isRead) return 'bg-gray-50';
    
    switch (type) {
      case 'comment':
        return 'bg-blue-50';
      case 'approval':
      case 'status_change':
        return 'bg-green-50';
      case 'new_post':
      case 'new_version':
      case 'new_design_request':
        return 'bg-purple-50';
      default:
        return 'bg-gray-50';
    }
  };

  const filteredNotifications = notifications;

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isRTL ? 'الإشعارات' : 'Notifications'}
          </h1>
          {unreadCount > 0 && (
            <p className="text-gray-600 mt-1">
              {isRTL 
                ? `${unreadCount} إشعار غير مقروء` 
                : `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
              }
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleMarkAllAsRead}
              loading={markingAll}
              disabled={markingAll}
            >
              <CheckIcon className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
              {isRTL ? 'وضع علامة مقروء للكل' : 'Mark All Read'}
            </Button>
          )}
          {filter === 'read' && notifications.length > 0 && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleClearRead}
              loading={clearing}
              disabled={clearing}
            >
              <TrashIcon className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
              {isRTL ? 'حذف المقروءة' : 'Clear Read'}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {isRTL ? 'الكل' : 'All'}
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
            filter === 'unread'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {isRTL ? 'غير المقروء' : 'Unread'}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setFilter('read')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'read'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {isRTL ? 'المقروء' : 'Read'}
        </button>
      </div>

      {/* Notifications List */}
      {error ? (
        <Card className="p-6">
          <p className="text-red-600">{error}</p>
        </Card>
      ) : filteredNotifications.length === 0 ? (
        <Card className="p-12 text-center">
          <BellIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">
            {isRTL 
              ? filter === 'unread' 
                ? 'لا توجد إشعارات غير مقروءة' 
                : filter === 'read'
                ? 'لا توجد إشعارات مقروءة'
                : 'لا توجد إشعارات'
              : filter === 'unread'
              ? 'No unread notifications'
              : filter === 'read'
              ? 'No read notifications'
              : 'No notifications'
            }
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => (
            <Card 
              key={notification.id} 
              className={`p-4 ${getNotificationColor(notification.type, notification.is_read)} transition-all cursor-pointer hover:shadow-md ${
                !notification.is_read ? 'border-l-4 border-primary-600' : ''
              }`}
              onClick={() => {
                if (notification.post_id) {
                  window.location.href = `/posts/${notification.post_id}`;
                }
                if (!notification.is_read) {
                  handleMarkAsRead(notification.id);
                }
              }}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getNotificationIcon(notification.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notification.is_read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                    {notification.message}
                  </p>
                  {notification.post_title && (
                    <Link
                      to={`/posts/${notification.post_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-primary-600 hover:text-primary-700 text-sm mt-1 block"
                    >
                      {notification.post_title}
                    </Link>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(notification.created_at)}
                  </p>
                </div>

                {/* Actions */}
                <div 
                  className="flex-shrink-0 flex gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {!notification.is_read && (
                    <button
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200"
                      title={isRTL ? 'وضع علامة مقروء' : 'Mark as read'}
                    >
                      <CheckIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(notification.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                    title={isRTL ? 'حذف' : 'Delete'}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;

